import type { ErrorResponse } from "@shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App } from "antd";
import type { AxiosError } from "axios";
import { useMemo } from "react";
import { organizationService } from "../services/organization.service";
import type {
	CreateOrganizationRequest,
	ListOrganizationsParams,
	OrgPickerTreeNode,
	UpdateOrganizationRequest,
} from "../types";

export const ORGANIZATION_QUERY_KEY = "organizations";
const DEFAULT_LIST_QUERY: ListOrganizationsParams = { page: 1, limit: 10 };

// ─── useOrganizations (list) ─────────────────────────────────────────
export function useOrganizations(params?: Partial<ListOrganizationsParams>) {
	const query = { ...DEFAULT_LIST_QUERY, ...params };
	return useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "list", query],
		queryFn: () => organizationService.list(query),
	});
}

// ─── useOrganization (getById) ────────────────────────────────────────
export function useOrganization(id?: string) {
	return useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "detail", id],
		queryFn: () => organizationService.getById(id as string),
		enabled: !!id,
	});
}

// ─── useChildOrganizations (quyi tashkilotlar) ───────────────────────
export function useChildOrganizations(
	parentId: string,
	params?: { page?: number; limit?: number }
) {
	return useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "children", parentId, params],
		queryFn: () => organizationService.listChildren(parentId, params),
		enabled: !!parentId,
		staleTime: 30_000, // 30s — use prefetched data when opening tab
	});
}

// ─── useMyChildOrganizations (quyi tashkilotlar) ─────────────────────
export function useMyChildOrganizations(params?: { page?: number; limit?: number }) {
	return useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "my-children", params],
		queryFn: () => organizationService.listMyChildren(params),
		staleTime: 30_000,
	});
}

// ─── useMyCrossAccessOrganizations (boshqa tashkilotlar — faqat cross access) ─
export function useMyCrossAccessOrganizations(params?: { page?: number; limit?: number }) {
	return useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "my-children-cross-access", params],
		queryFn: () => organizationService.listMyCrossAccessOrganizations(params),
		staleTime: 30_000,
	});
}

// ─── useCreateOrganization ────────────────────────────────────────────
export function useCreateOrganization() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (data: CreateOrganizationRequest) => organizationService.create(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [ORGANIZATION_QUERY_KEY] });
			message.success("Tashkilot yaratildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Tashkilot saqlashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useUpdateOrganization ────────────────────────────────────────────
export function useUpdateOrganization() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationRequest }) =>
			organizationService.update(id, data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [ORGANIZATION_QUERY_KEY] });
			message.success("Tashkilot yangilandi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Tashkilotni yangilashda xatolik";
			message.error(msg);
		},
	});
}

// ─── useDeleteOrganization ────────────────────────────────────────────
export function useDeleteOrganization() {
	const { message } = App.useApp();
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => organizationService.delete(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: [ORGANIZATION_QUERY_KEY] });
			message.success("Tashkilot o'chirildi");
		},
		onError: (error: AxiosError<ErrorResponse>) => {
			const msg = error.response?.data?.error?.message || "Tashkilotni o'chirishda xatolik";
			message.error(msg);
		},
	});
}

// ─── useOrganizationTree (picker search) ─────────────────────────────
function flattenTreeToMap(
	nodes: OrgPickerTreeNode[],
	map: Map<string, OrgPickerTreeNode>
): Map<string, OrgPickerTreeNode> {
	for (const node of nodes) {
		map.set(node.id, node);
		flattenTreeToMap(node.children, map);
	}
	return map;
}

export function useOrganizationTree() {
	const query = useQuery({
		queryKey: [ORGANIZATION_QUERY_KEY, "picker-tree"],
		queryFn: () => organizationService.getPickerTree(),
		staleTime: 5 * 60_000, // 5m — org hierarchy changes rarely within a session
	});

	const nodesById = useMemo(() => flattenTreeToMap(query.data ?? [], new Map()), [query.data]);

	return { data: query.data, isLoading: query.isLoading, nodesById };
}
